import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import CategoryFilterBar from '../CategoryFilterBar';
import InlineLoading from '../InlineLoading';
import MissingUnitWeightEditor from '../MissingUnitWeightEditor';
import ProductSearchInput from '../ProductSearchInput';
import useProductSearchSettings from '../../hooks/useProductSearchSettings';
import { buildProductFormatOptions } from '../../utils/clientType';
import {
  CLIENT_PRICING_FALLBACK_IMAGE,
  CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
  CLIENT_PRICING_MODE_COST_PLUS,
  CLIENT_PRICING_MODE_MARGIN_DISCOUNT,
  CLIENT_PRICING_RULE_FIXED,
  CLIENT_PRICING_RULE_MARGIN,
  buildClientPricingCategoryOptions,
  buildClientPricingProductGroups,
  calculateClientPricingDiscountFromFinalPrice,
  calculateClientPricingFinalPriceFromBenefit,
  calculateClientPricingReferenceMarkup,
  filterClientPricingRows,
  formatClientPricingMoney,
  getClientPricingRowState,
  getClientPricingSearchProducts,
  isClientPricingMarginRule,
  marginDisplayPercentToReduction,
  marginReductionPercentToDisplay,
  normalizeClientPricingDiscountValueInput,
  normalizeClientPricingMarginPercentInput,
  normalizeClientPricingMarginDisplay,
} from '../../utils/clientPricingMatrix';

const getRowId = (row) => Number(row?.varietyPriceId);

const normalizeBulkRuleValueInput = (value) => {
  const normalized = String(value ?? '').replace(/[^\d.,-]/g, '').replace(',', '.');
  const isNegative = normalized.startsWith('-');
  const unsigned = normalized.replace(/-/g, '');
  const [whole = '', ...decimalParts] = unsigned.split('.');
  const decimal = decimalParts.join('');
  const compact = decimalParts.length ? `${whole}.${decimal}` : whole;
  return `${isNegative ? '-' : ''}${compact}`;
};

const ROW_GRADIENT_THEMES = [
  {
    accent: '#2563eb',
    product: ['#bfdbfe', '#dbeafe', '#eff6ff'],
    variant: ['#dbeafe', '#eff6ff', '#ffffff'],
    price: ['#ffffff', '#fbfdff', '#eff6ff'],
    selected: ['#bfdbfe', '#dbeafe', '#f8fbff'],
  },
  {
    accent: '#2563eb',
    product: ['#bfdbfe', '#dbeafe', '#eff6ff'].reverse(),
    variant: ['#dbeafe', '#eff6ff', '#ffffff'].reverse(),
    price: ['#ffffff', '#fbfdff', '#eff6ff'].reverse(),
    selected: ['#bfdbfe', '#dbeafe', '#f8fbff'].reverse(),
  }
];

const INTERCALATED_ROW_GRADIENT_THEME_ORDER = [0, 1];

const getRowGradientTheme = (groupIndex = 0) => {
  const safeIndex = Number.isInteger(groupIndex) && groupIndex >= 0 ? groupIndex : 0;
  const themeIndex = INTERCALATED_ROW_GRADIENT_THEME_ORDER[
    safeIndex % INTERCALATED_ROW_GRADIENT_THEME_ORDER.length
  ];
  return ROW_GRADIENT_THEMES[themeIndex] || ROW_GRADIENT_THEMES[0];
};

const buildGradientRowStyle = (theme, level, { selected = false } = {}) => {
  const stops = selected ? theme.selected : theme[level];
  return {
    '--client-pricing-row-accent': theme.accent,
    '--client-pricing-row-start': stops[0],
    '--client-pricing-row-mid': stops[1],
    '--client-pricing-row-end': stops[2],
  };
};

const getAllocationBasisLabel = (value) => {
  if (value === 'weight') return 'peso';
  if (value === 'quantity') return 'cantidad';
  if (value === 'importaciones') return 'importaciones';
  return '';
};

const formatReferenceCostDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('es-CL');
};

const buildReferenceCostTitle = (referenceCost) => {
  if (!referenceCost) return '';
  if (referenceCost.referenceCostSource === 'estimated' || referenceCost.conversionStatus === 'estimated') {
    return 'Costo estimado configurado en el precio';
  }
  const parts = [];
  if (referenceCost.sourceStockListName) parts.push(`Lista: ${referenceCost.sourceStockListName}`);
  const dateLabel = formatReferenceCostDate(referenceCost.sourceStockListCreatedAt);
  if (dateLabel) parts.push(`Fecha: ${dateLabel}`);
  const basisLabel = getAllocationBasisLabel(referenceCost.allocationBasis);
  if (basisLabel) parts.push(`Prorrateo por ${basisLabel}`);
  if (referenceCost.conversionStatus === 'missing_unit_weight') {
    parts.push('Falta peso de unidad para convertir a este precio');
  }
  return parts.join(' - ');
};

const renderReferenceCostLabel = (referenceCost, { disabled = false, onSave } = {}) => {
  if (!referenceCost) return <span className="text-gray-400">-</span>;
  if (referenceCost.conversionStatus === 'missing_unit_weight') {
    return <MissingUnitWeightEditor disabled={disabled} onSave={onSave} />;
  }
  if (referenceCost.referenceCost === null || referenceCost.referenceCost === undefined) {
    return <span className="text-gray-400">-</span>;
  }
  if (referenceCost.referenceCostSource === 'estimated' || referenceCost.conversionStatus === 'estimated') {
    return <span className="text-xs font-semibold text-blue-700">{formatClientPricingMoney(referenceCost.referenceCost)}</span>;
  }
  if (referenceCost.calculationMethod === 'unit_weight_kg' && Number(referenceCost.unitWeightKg) > 0) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        <span>{formatClientPricingMoney(referenceCost.referenceCost)}</span>
        <MissingUnitWeightEditor
          disabled={disabled}
          initialValue={referenceCost.unitWeightKg}
          triggerLabel={`Peso: ${referenceCost.unitWeightKg} kg · Editar`}
          onSave={onSave}
        />
      </span>
    );
  }
  return <span>{formatClientPricingMoney(referenceCost.referenceCost)}</span>;
};

const formatReferenceMarkupPercent = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  const rounded = Math.round(Number(value) * 10) / 10;
  return `${rounded.toLocaleString('de-DE', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
};

const renderReferenceMarkupLabel = ({ amount, percent }) => {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) {
    return <span className="text-gray-400">-</span>;
  }
  const percentLabel = formatReferenceMarkupPercent(percent);
  const isNegative = Number(amount) < 0;
  return (
    <span className={`inline-flex flex-col items-end leading-tight ${isNegative ? 'text-red-600' : 'text-gray-700'}`}>
      <span>{formatClientPricingMoney(amount)}</span>
      {percentLabel !== '-' && (
        <span className={`text-[10px] font-medium ${isNegative ? 'text-red-500' : 'text-gray-500'}`}>
          {percentLabel} sobre el costo
        </span>
      )}
    </span>
  );
};

const renderFinalPriceLabel = ({ finalPrice, referenceCost }) => {
  const markup = calculateClientPricingReferenceMarkup({
    basePrice: finalPrice,
    referenceCost,
  });
  const percentLabel = formatReferenceMarkupPercent(markup.percent);
  const isNegative = Number(markup.amount) < 0;

  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{formatClientPricingMoney(finalPrice)}</span>
      {markup.amount !== null && percentLabel !== '-' && (
        <span className={`mt-0.5 text-[10px] font-medium ${isNegative ? 'text-red-500' : 'text-green-600'}`}>
          Margen: {formatClientPricingMoney(markup.amount)} · {percentLabel}
        </span>
      )}
    </span>
  );
};

const getRowsSelectionState = (rows, selectedIds) => {
  const ids = (rows || []).map(getRowId).filter((id) => Number.isInteger(id));
  if (!ids.length) return { allSelected: false, someSelected: false };
  const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
  return {
    allSelected: selectedCount === ids.length,
    someSelected: selectedCount > 0,
  };
};

const resolveStatus = ({ row, rowSavingById, rowStatusById, customMode }) => {
  const id = getRowId(row);
  const saving = Boolean(rowSavingById?.[id]);
  const status = rowStatusById?.[id] || 'idle';
  if (saving || status === 'saving') return { label: 'Guardando...', className: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' };
  if (status === 'saved') return { label: 'Guardado', className: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' };
  if (status === 'error') return { label: 'Error', className: 'bg-red-50 text-red-700 ring-1 ring-red-200' };
  if (status === 'pending') return { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' };
  if (row?.conversionIssue) {
    return { label: 'Fijo — pendiente de conversión', className: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' };
  }
  if (isClientPricingMarginRule(row) && row?.calculationStatus === 'missing_cost') {
    return { label: 'Sin costo', className: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' };
  }
  if (isClientPricingMarginRule(row) && row?.calculationStatus === 'non_positive_margin') {
    return { label: 'Sin margen positivo', className: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200' };
  }
  if (customMode) {
    return row?.hasDiscount
      ? { label: 'Borrador', className: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' }
      : { label: 'Sin descuento', className: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200' };
  }
  return row?.hasDiscount
    ? { label: 'Activo', className: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' }
    : { label: 'Auto', className: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200' };
};

const getRowDiscountType = (row) => row?.discountType || 'amount';

const getRowDiscountValue = (row) => normalizeClientPricingDiscountValueInput(row?.discountValue ?? '');

const buildRowPatch = (row, draft = {}, { marginMode = false } = {}) => {
  if (marginMode) {
    return {
      ruleType: CLIENT_PRICING_RULE_MARGIN,
      marginReductionPercent: draft.marginReductionPercent ?? row?.marginReductionPercent ?? '',
    };
  }
  return {
    discountType: draft.discountType ?? getRowDiscountType(row),
    discountValue: normalizeClientPricingDiscountValueInput(draft.discountValue ?? row?.discountValue ?? ''),
  };
};

const rowPatchMatches = (row, patch, { marginMode = false } = {}) => {
  if (marginMode) {
    const currentValue = isClientPricingMarginRule(row)
      ? String(row?.marginReductionPercent ?? '')
      : '';
    return currentValue === String(patch?.marginReductionPercent ?? '');
  }
  return String(getRowDiscountType(row)) === String(patch.discountType || 'amount')
    && getRowDiscountValue(row) === normalizeClientPricingDiscountValueInput(patch.discountValue ?? '');
};

const getEditableBenefitValue = ({ benefitType, markup }) => {
  const value = benefitType === 'percent' ? markup.percent : markup.amount;
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '';
  const rounded = benefitType === 'percent'
    ? Math.round(Number(value) * 10) / 10
    : Math.round(Number(value));
  return String(rounded);
};

const ClientPricingDiscountMatrix = ({
  title = 'Descuentos',
  description = '',
  rows = [],
  loading = false,
  error = '',
  saved = false,
  disabled = false,
  readOnly = false,
  customMode = false,
  pricingMode = 'discount',
  marginPercentDisplay = CLIENT_PRICING_MARGIN_DISPLAY_REDUCTION,
  search = '',
  onSearchChange,
  bulkForm,
  onBulkFormChange,
  onBulkAction,
  onRowChange,
  onUnitWeightSave,
  rowSavingById = {},
  rowStatusById = {},
  savingBulk = false,
  emptyMessage = 'No hay precios visibles para este filtro.',
  className = '',
}) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedClientType, setSelectedClientType] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [expandedProductKey, setExpandedProductKey] = useState('');
  const [expandedVariantKey, setExpandedVariantKey] = useState('');
  const [localBulkForm, setLocalBulkForm] = useState({
    discountType: 'amount',
    discountValue: '',
    benefitType: 'amount',
    benefitValue: '',
    marginPercent: '',
  });
  const [benefitTypeById, setBenefitTypeById] = useState({});
  const [rowDraftsById, setRowDraftsById] = useState({});
  const rowDraftsRef = useRef({});
  const [localError, setLocalError] = useState('');
  const [replaceDynamicRulesInBulk, setReplaceDynamicRulesInBulk] = useState(false);
  const { searchMode } = useProductSearchSettings();

  const resolvedBulkForm = bulkForm || localBulkForm;
  const costPlusMode = pricingMode === CLIENT_PRICING_MODE_COST_PLUS;
  const marginDiscountMode = pricingMode === CLIENT_PRICING_MODE_MARGIN_DISCOUNT;
  const normalizedMarginDisplay = normalizeClientPricingMarginDisplay(marginPercentDisplay);
  const previousMarginDisplayRef = useRef(normalizedMarginDisplay);
  const dataColumnCount = marginDiscountMode ? 9 : costPlusMode ? 7 : 8;
  const originalRowsById = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((row) => {
      const id = getRowId(row);
      if (Number.isInteger(id)) map.set(id, row);
    });
    return map;
  }, [rows]);
  const displayRows = useMemo(() => (rows || []).map((row) => {
    const id = getRowId(row);
    const draft = Number.isInteger(id) ? rowDraftsById[id] : null;
    return draft ? { ...row, ...draft } : row;
  }), [rowDraftsById, rows]);
  const categoryOptions = useMemo(() => buildClientPricingCategoryOptions(displayRows), [displayRows]);
  const clientTypeOptions = useMemo(() => {
    const formats = displayRows.map((row) => ({
      code: row?.productFormatCode || row?.clientType,
      name: row?.productFormatName || row?.clientTypeLabel,
    })).filter((format) => String(format.code || '').trim());
    return formats.length ? buildProductFormatOptions(formats) : [];
  }, [displayRows]);
  const filtersReady = categoryOptions.some((option) => String(option.id) === selectedCategory)
    && clientTypeOptions.some((option) => option.value === selectedClientType);
  const scopedRows = useMemo(() => (filtersReady ? filterClientPricingRows(displayRows, {
    categoryId: selectedCategory,
    clientType: selectedClientType,
  }) : []), [displayRows, filtersReady, selectedCategory, selectedClientType]);
  const searchProducts = useMemo(() => getClientPricingSearchProducts(scopedRows), [scopedRows]);
  const filteredRows = useMemo(() => filterClientPricingRows(scopedRows, {
    search,
    categoryId: selectedCategory,
    clientType: selectedClientType,
    searchMode,
  }), [scopedRows, search, selectedCategory, selectedClientType, searchMode]);
  const productGroups = useMemo(() => buildClientPricingProductGroups(filteredRows), [filteredRows]);
  const allSelectedRows = useMemo(
    () => displayRows.filter((row) => selectedIds.has(getRowId(row))),
    [displayRows, selectedIds]
  );
  const selectedDynamicRuleCount = useMemo(
    () => allSelectedRows.filter(isClientPricingMarginRule).length,
    [allSelectedRows]
  );

  useEffect(() => {
    if (!selectedDynamicRuleCount) setReplaceDynamicRulesInBulk(false);
  }, [selectedDynamicRuleCount]);

  const updateRowDrafts = useCallback((updater) => {
    const current = rowDraftsRef.current;
    const next = typeof updater === 'function' ? updater(current) : updater;
    rowDraftsRef.current = next;
    setRowDraftsById(next);
  }, []);

  useEffect(() => {
    const previousDisplay = previousMarginDisplayRef.current;
    if (previousDisplay === normalizedMarginDisplay) return;

    updateRowDrafts((current) => {
      let changed = false;
      const next = Object.fromEntries(Object.entries(current).map(([id, draft]) => {
        if (!Object.hasOwn(draft, '_marginDisplayValue') || draft._marginError) {
          return [id, draft];
        }
        const nextDisplayValue = draft.marginReductionPercent === ''
          ? ''
          : marginReductionPercentToDisplay(
            draft.marginReductionPercent,
            normalizedMarginDisplay
          );
        if (nextDisplayValue === draft._marginDisplayValue) return [id, draft];
        changed = true;
        return [id, { ...draft, _marginDisplayValue: nextDisplayValue }];
      }));
      return changed ? next : current;
    });

    const currentBulkValue = resolvedBulkForm.marginPercent;
    if (String(currentBulkValue ?? '').trim() !== '') {
      const canonicalValue = marginDisplayPercentToReduction(
        currentBulkValue,
        previousDisplay
      );
      const nextBulkValue = marginReductionPercentToDisplay(
        canonicalValue,
        normalizedMarginDisplay
      );
      const nextBulkForm = { ...resolvedBulkForm, marginPercent: nextBulkValue };
      if (bulkForm && onBulkFormChange) onBulkFormChange(nextBulkForm);
      else setLocalBulkForm(nextBulkForm);
    }
    previousMarginDisplayRef.current = normalizedMarginDisplay;
  }, [
    bulkForm,
    normalizedMarginDisplay,
    onBulkFormChange,
    resolvedBulkForm,
    updateRowDrafts,
  ]);

  useEffect(() => {
    setSelectedCategory((current) => (
      categoryOptions.some((option) => String(option.id) === current) ? current : ''
    ));
    setSelectedClientType((current) => (
      clientTypeOptions.some((option) => option.value === current) ? current : ''
    ));
  }, [categoryOptions, clientTypeOptions]);

  useEffect(() => {
    if (filtersReady) return;
    setSelectedIds((current) => (current.size ? new Set() : current));
    setReplaceDynamicRulesInBulk(false);
  }, [filtersReady]);

  useEffect(() => {
    const availableIds = new Set((rows || []).map(getRowId).filter((id) => Number.isInteger(id)));
    setSelectedIds((current) => {
      const next = new Set(Array.from(current).filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
    updateRowDrafts((current) => {
      const next = {};
      let changed = false;
      Object.entries(current).forEach(([rawId, draft]) => {
        const id = Number(rawId);
        const row = originalRowsById.get(id);
        if (!row) {
          changed = true;
          return;
        }
        const patch = buildRowPatch(row, draft, { marginMode: marginDiscountMode });
        if (rowPatchMatches(row, patch, { marginMode: marginDiscountMode })) {
          changed = true;
          return;
        }
        next[rawId] = draft;
      });
      return changed ? next : current;
    });
  }, [marginDiscountMode, originalRowsById, rows, updateRowDrafts]);

  useEffect(() => {
    const availableProductKeys = new Set(productGroups.map((group) => String(group.key)));
    const availableVariantKeys = new Set();
    productGroups.forEach((group) => {
      group.variants.forEach((variant) => availableVariantKeys.add(String(variant.key)));
    });

    setExpandedProductKey((current) => (
      current && availableProductKeys.has(current) ? current : ''
    ));
    setExpandedVariantKey((current) => (
      current && availableVariantKeys.has(current) ? current : ''
    ));
  }, [productGroups]);

  const toggleProductExpansion = (groupKey) => {
    const nextKey = String(groupKey);
    setExpandedProductKey((current) => (current === nextKey ? '' : nextKey));
    setExpandedVariantKey('');
  };

  const toggleVariantExpansion = (variantKey) => {
    const nextKey = String(variantKey);
    setExpandedVariantKey((current) => (current === nextKey ? '' : nextKey));
  };

  const setBulkField = (field, value) => {
    let nextValue = value;
    if (field === 'marginPercent') {
      nextValue = normalizeClientPricingMarginPercentInput(value);
    } else if (field === 'discountValue' || field === 'benefitValue') {
      nextValue = normalizeBulkRuleValueInput(value);
    }
    const next = { ...resolvedBulkForm, [field]: nextValue };
    if (bulkForm && onBulkFormChange) {
      onBulkFormChange(next);
      return;
    }
    setLocalBulkForm(next);
  };

  const setRowDraftField = (row, field, value) => {
    const id = getRowId(row);
    if (!Number.isInteger(id)) return;
    const originalRow = originalRowsById.get(id) || row;
    let nextValue = value;
    if (field === 'discountValue') nextValue = normalizeClientPricingDiscountValueInput(value);

    updateRowDrafts((current) => {
      const currentDraft = current[id] || {};
      const nextDraft = { ...currentDraft, [field]: nextValue };
      const patch = buildRowPatch(originalRow, nextDraft);
      const next = { ...current };
      if (rowPatchMatches(originalRow, patch)) {
        delete next[id];
      } else {
        next[id] = patch;
      }
      return next;
    });
  };

  const setRowMarginDraft = (row, value) => {
    const id = getRowId(row);
    if (!Number.isInteger(id)) return;
    const originalRow = originalRowsById.get(id) || row;
    const displayValue = normalizeClientPricingMarginPercentInput(value);
    const numericValue = displayValue === '' ? null : Number(displayValue);
    const marginError = numericValue !== null && (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > 100)
      ? 'El porcentaje debe estar entre 0% y 100%.'
      : '';
    const marginReductionPercent = marginDisplayPercentToReduction(
      displayValue,
      normalizedMarginDisplay
    );
    const nextDraft = {
      ruleType: CLIENT_PRICING_RULE_MARGIN,
      marginReductionPercent,
      _marginDisplayValue: displayValue,
      _marginError: marginError,
    };

    updateRowDrafts((current) => {
      const patch = buildRowPatch(originalRow, nextDraft, { marginMode: true });
      const next = { ...current };
      if (!marginError && rowPatchMatches(originalRow, patch, { marginMode: true })) {
        delete next[id];
      } else {
        next[id] = nextDraft;
      }
      return next;
    });
  };

  const setRowCostPlusDraft = (row, field, value) => {
    const id = getRowId(row);
    if (!Number.isInteger(id)) return;
    const originalRow = originalRowsById.get(id) || row;
    const normalizedValue = normalizeClientPricingDiscountValueInput(value);
    const benefitType = benefitTypeById[id] || 'amount';
    let finalPrice = null;
    let benefitValue = '';
    let costPlusError = '';

    if (field === 'benefitValue') {
      benefitValue = normalizedValue;
      finalPrice = calculateClientPricingFinalPriceFromBenefit({
        referenceCost: originalRow.referenceCost,
        benefitType,
        benefitValue: normalizedValue,
      });
      if (finalPrice === null) {
        costPlusError = 'Este precio no tiene un costo de referencia disponible.';
      }
    } else {
      finalPrice = normalizedValue === '' ? 0 : Number(normalizedValue);
      const markup = calculateClientPricingReferenceMarkup({
        basePrice: finalPrice,
        referenceCost: originalRow.referenceCost,
      });
      benefitValue = getEditableBenefitValue({ benefitType, markup });
    }

    if (!costPlusError && finalPrice > Number(originalRow.basePrice || 0)) {
      costPlusError = 'El precio final no puede superar el precio base.';
    }
    const discountValue = calculateClientPricingDiscountFromFinalPrice({
      basePrice: originalRow.basePrice,
      finalPrice,
    });

    updateRowDrafts((current) => ({
      ...current,
      [id]: {
        discountType: 'amount',
        discountValue: normalizeClientPricingDiscountValueInput(discountValue),
        _benefitValue: benefitValue,
        _finalPrice: normalizedValue === '' && field === 'finalPrice' ? '' : String(finalPrice ?? ''),
        _costPlusError: costPlusError,
      },
    }));
    setLocalError(costPlusError);
  };

  const setRowMarginType = (row, nextType) => {
    const id = getRowId(row);
    if (!Number.isInteger(id)) return;
    setBenefitTypeById((current) => ({ ...current, [id]: nextType }));

    updateRowDrafts((current) => {
      const currentDraft = current[id];
      if (!currentDraft) return current;
      const finalPrice = Object.hasOwn(currentDraft, '_finalPrice')
        ? Number(currentDraft._finalPrice || 0)
        : getClientPricingRowState({ ...row, ...currentDraft }).finalPrice;
      const markup = calculateClientPricingReferenceMarkup({
        basePrice: finalPrice,
        referenceCost: row.referenceCost,
      });
      return {
        ...current,
        [id]: {
          ...currentDraft,
          _benefitValue: getEditableBenefitValue({ benefitType: nextType, markup }),
        },
      };
    });
  };

  const clearRowDraft = (id) => {
    updateRowDrafts((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const commitRowDraft = (row) => {
    const id = getRowId(row);
    if (!Number.isInteger(id) || disabled || rowSavingById?.[id]) return;
    const draft = rowDraftsRef.current[id];
    if (!draft) return;
    if (draft._costPlusError) {
      setLocalError(draft._costPlusError);
      return;
    }
    if (draft._marginError) {
      setLocalError(draft._marginError);
      return;
    }
    const originalRow = originalRowsById.get(id) || row;
    const patch = buildRowPatch(originalRow, draft, { marginMode: marginDiscountMode });
    clearRowDraft(id);
    if (rowPatchMatches(originalRow, patch, { marginMode: marginDiscountMode })) return;
    setLocalError('');
    onRowChange?.(id, patch, { ...originalRow, ...patch });
  };

  const replaceDynamicRule = (row) => {
    const id = getRowId(row);
    if (!Number.isInteger(id) || disabled || readOnly || rowSavingById?.[id]) return;
    const rowState = getClientPricingRowState(row);
    const patch = {
      ruleType: CLIENT_PRICING_RULE_FIXED,
      discountType: 'amount',
      discountValue: String(rowState.discountAmount || ''),
      replaceExistingRule: true,
    };
    onRowChange?.(id, patch, { ...row, ...patch, marginReductionPercent: null });
  };

  const handleRowCommitKeyDown = (event, row) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commitRowDraft(row);
    event.currentTarget.blur();
  };

  const setRowsSelected = (targetRows, checked) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      (targetRows || []).forEach((row) => {
        const id = getRowId(row);
        if (!Number.isInteger(id)) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const toggleRow = (row) => {
    const id = getRowId(row);
    if (!Number.isInteger(id)) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulk = async (action) => {
    if (!filtersReady) {
      setLocalError('Selecciona una categoría y un formato antes de aplicar cambios.');
      return;
    }
    const selectedRows = displayRows.filter((row) => selectedIds.has(getRowId(row)));
    if (!selectedRows.length) {
      setLocalError('Selecciona al menos un precio.');
      return;
    }
    const bulkValue = marginDiscountMode
      ? resolvedBulkForm.marginPercent
      : costPlusMode
        ? resolvedBulkForm.benefitValue
        : resolvedBulkForm.discountValue;
    if (action !== 'clear' && (String(bulkValue).trim() === '' || !Number.isFinite(Number(bulkValue)))) {
      setLocalError(marginDiscountMode
        ? 'Ingresa un porcentaje de margen válido.'
        : costPlusMode ? 'Ingresa un margen valido.' : 'Ingresa un descuento valido.');
      return;
    }
    if (action !== 'clear' && costPlusMode && Number(bulkValue) < 0) {
      setLocalError('Ingresa un margen mayor o igual a 0.');
      return;
    }
    if (action !== 'clear' && marginDiscountMode && (Number(bulkValue) < 0 || Number(bulkValue) > 100)) {
      setLocalError('El porcentaje debe estar entre 0% y 100%.');
      return;
    }
    const marginReductionPercent = marginDiscountMode
      ? marginDisplayPercentToReduction(bulkValue, normalizedMarginDisplay)
      : undefined;
    setLocalError('');
    await onBulkAction?.({
      action: marginDiscountMode && action !== 'clear'
        ? 'set_margin_discount'
        : costPlusMode && action !== 'clear' ? 'set_benefit' : action,
      selectedRows,
      selectedIds: selectedRows.map((row) => getRowId(row)),
      ruleType: marginDiscountMode ? CLIENT_PRICING_RULE_MARGIN : CLIENT_PRICING_RULE_FIXED,
      replaceExistingRules: !marginDiscountMode && replaceDynamicRulesInBulk,
      marginReductionPercent,
      discountType: resolvedBulkForm.discountType || 'amount',
      discountValue: resolvedBulkForm.discountValue,
      benefitType: resolvedBulkForm.benefitType || 'amount',
      benefitValue: resolvedBulkForm.benefitValue,
    });
    setReplaceDynamicRulesInBulk(false);
  };

  const visibleSelection = getRowsSelectionState(filteredRows, selectedIds);

  const renderProductRow = (group, groupIndex) => {
    const isExpanded = expandedProductKey === String(group.key);
    const selection = getRowsSelectionState(group.priceRows, selectedIds);
    const rowTheme = getRowGradientTheme(groupIndex);
    const imageSrc = group.productImage
      || group.variants[0]?.varietyImage
      || group.priceRows[0]?.priceImage
      || CLIENT_PRICING_FALLBACK_IMAGE;

    return (
      <React.Fragment key={group.key}>
        <tr
          className="client-pricing-gradient-row client-pricing-gradient-product border-b"
          style={buildGradientRowStyle(rowTheme, 'product')}
        >
          <td className="w-10 px-3 py-2 align-top">
            {!readOnly && (
              <input
                type="checkbox"
                checked={selection.allSelected}
                aria-checked={selection.allSelected ? 'true' : selection.someSelected ? 'mixed' : 'false'}
                onChange={(event) => setRowsSelected(group.priceRows, event.target.checked)}
                disabled={disabled}
              />
            )}
          </td>
          <td colSpan={dataColumnCount} className="px-2 py-2">
            <button
              type="button"
              onClick={() => toggleProductExpansion(group.key)}
              className="flex w-full items-start gap-3 text-left"
            >
              <img
                src={imageSrc}
                alt={group.productName || 'Producto'}
                className="h-12 w-12 flex-none rounded border border-gray-200 object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-semibold text-gray-900">{group.productName || '-'}</span>
                <span className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span>{group.variants.length === 1 ? '1 variedad' : `${group.variants.length} variedades`}</span>
                  <span>{group.priceRows.length === 1 ? '1 precio' : `${group.priceRows.length} precios`}</span>
                  {group.categoryName && <span>{group.categoryName}</span>}
                </span>
              </span>
              <FiChevronDown className={`mt-1 flex-none text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </td>
        </tr>
        {isExpanded && group.variants.map((variant) => renderVariantRow(group, variant, rowTheme))}
      </React.Fragment>
    );
  };

  const renderVariantRow = (group, variant, rowTheme) => {
    const isExpanded = expandedVariantKey === String(variant.key);
    const selection = getRowsSelectionState(variant.priceRows, selectedIds);
    const imageSrc = variant.varietyImage
      || group.productImage
      || variant.priceRows[0]?.priceImage
      || CLIENT_PRICING_FALLBACK_IMAGE;
    return (
      <React.Fragment key={variant.key}>
        <tr
          className="client-pricing-gradient-row client-pricing-gradient-variant border-b"
          style={buildGradientRowStyle(rowTheme, 'variant')}
        >
          <td className="w-10 py-2 pl-8 pr-3 align-top">
            {!readOnly && (
              <input
                type="checkbox"
                checked={selection.allSelected}
                aria-checked={selection.allSelected ? 'true' : selection.someSelected ? 'mixed' : 'false'}
                onChange={(event) => setRowsSelected(variant.priceRows, event.target.checked)}
                disabled={disabled}
              />
            )}
          </td>
          <td colSpan={dataColumnCount} className="px-2 py-2">
            <button
              type="button"
              onClick={() => toggleVariantExpansion(variant.key)}
              className="ml-8 flex w-[calc(100%-2rem)] items-center gap-3 text-left"
            >
              <img
                src={imageSrc}
                alt={variant.varietyName || group.productName || 'Variedad'}
                className="h-10 w-10 flex-none rounded border border-gray-200 object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-semibold text-gray-800">{variant.varietyName || '-'}</span>
                <span className="text-xs text-gray-500">{variant.priceRows.length === 1 ? '1 precio' : `${variant.priceRows.length} precios`}</span>
              </span>
              <FiChevronDown className={`flex-none text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          </td>
        </tr>
        {isExpanded && variant.priceRows.map((row) => renderPriceRow(row, rowTheme))}
      </React.Fragment>
    );
  };

  const renderPriceRow = (row, rowTheme) => {
    const rowState = getClientPricingRowState(row);
    const id = getRowId(row);
    const selected = selectedIds.has(id);
    const hasLocalDraft = Boolean(rowDraftsById[id]);
    const status = resolveStatus({
      row: {
        ...row,
        ...rowState,
        conversionIssue: marginDiscountMode ? row?.conversionIssue : null,
      },
      rowSavingById,
      rowStatusById: hasLocalDraft ? { ...rowStatusById, [id]: 'pending' } : rowStatusById,
      customMode,
    });
    const disabledRow = disabled || readOnly || Boolean(rowSavingById?.[id]);
    const referenceMarkup = calculateClientPricingReferenceMarkup({
      basePrice: row.basePrice,
      referenceCost: row.referenceCost,
    });
    const benefitType = benefitTypeById[id] || 'amount';
    const finalMarkup = calculateClientPricingReferenceMarkup({
      basePrice: rowState.finalPrice,
      referenceCost: row.referenceCost,
    });
    const benefitInputValue = Object.hasOwn(row, '_benefitValue')
      ? row._benefitValue
      : getEditableBenefitValue({ benefitType, markup: finalMarkup });
    const finalPriceInputValue = Object.hasOwn(row, '_finalPrice')
      ? row._finalPrice
      : String(rowState.finalPrice ?? '');
    const hasNegativeFinalMargin = finalMarkup.amount !== null && Number(finalMarkup.amount) < 0;
    const hasFinalPriceError = Boolean(row._costPlusError);
    const dynamicRule = isClientPricingMarginRule(row);
    const fixedRulePendingConversion = !dynamicRule
      && (row.hasRule === true || Number(row.discountValue || 0) > 0);
    const marginInputValue = Object.hasOwn(row, '_marginDisplayValue')
      ? row._marginDisplayValue
      : dynamicRule
        ? marginReductionPercentToDisplay(row.marginReductionPercent, normalizedMarginDisplay)
        : '';
    const dynamicRuleLabel = normalizedMarginDisplay === 'retained'
      ? `Deja ${marginReductionPercentToDisplay(row.marginReductionPercent, normalizedMarginDisplay)}% del margen`
      : `Reduce ${marginReductionPercentToDisplay(row.marginReductionPercent, normalizedMarginDisplay)}% del margen`;
    const marginWarning = rowState.calculationStatus === 'missing_cost'
      ? 'No hay costo de referencia. Se aplicará descuento $0 hasta que exista uno.'
      : rowState.calculationStatus === 'non_positive_margin'
        ? 'El precio no tiene margen positivo. Se aplicará descuento $0.'
        : '';

    return (
      <tr
        key={id}
        className="client-pricing-gradient-row client-pricing-gradient-price border-b"
        style={buildGradientRowStyle(rowTheme, 'price', { selected })}
      >
        <td className="w-10 py-2 pl-12 pr-3 align-top">
          {!readOnly && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => toggleRow(row)}
              disabled={disabled}
            />
          )}
        </td>
        <td className="px-3 py-2 text-sm text-gray-700">
          <span className="inline-flex rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700">
            {rowState.clientTypeLabel}
          </span>
        </td>
        <td className="px-3 py-2 text-sm text-gray-700">
          {[row.metricUnit, row.description].filter(Boolean).join(' - ') || '-'}
          {row.barcode && <span className="block text-xs text-gray-400">{row.barcode}</span>}
        </td>
        <td
          className="px-3 py-2 text-right text-sm font-medium text-gray-700"
          title={buildReferenceCostTitle(row.referenceCost)}
        >
          {renderReferenceCostLabel(row.referenceCost, {
            disabled: disabledRow || typeof onUnitWeightSave !== 'function',
            onSave: (unitWeightKg) => onUnitWeightSave?.(row, unitWeightKg),
          })}
        </td>
        <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
          {formatClientPricingMoney(row.basePrice)}
        </td>
        {marginDiscountMode ? (
          <>
            <td className="px-3 py-2 text-right text-xs font-medium">
              {rowState.positiveMargin === null
                ? <span className="text-gray-400">-</span>
                : formatClientPricingMoney(rowState.positiveMargin)}
            </td>
            <td className="px-3 py-2">
              {readOnly ? (
                dynamicRule ? (
                  <span className="text-sm font-semibold text-blue-700">{dynamicRuleLabel}</span>
                ) : (
                  <span className={`text-xs font-semibold ${fixedRulePendingConversion ? 'text-amber-700' : 'text-gray-600'}`}>
                    {fixedRulePendingConversion ? 'Fijo — pendiente de conversión' : 'Sin regla'}
                  </span>
                )
              ) : (
                <div className="min-w-[210px]">
                  <div className="flex items-center gap-2">
                    <input
                      aria-label={normalizedMarginDisplay === 'retained'
                        ? 'Porcentaje de margen conservado'
                        : 'Porcentaje de reducción de margen'}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={marginInputValue}
                      onChange={(event) => setRowMarginDraft(row, event.target.value)}
                      onBlur={() => commitRowDraft(row)}
                      onKeyDown={(event) => handleRowCommitKeyDown(event, row)}
                      className={`h-9 w-24 rounded border px-2 text-sm ${row._marginError ? 'border-red-300' : 'border-gray-300'}`}
                      disabled={disabledRow}
                    />
                    <span className="text-sm font-semibold text-gray-500">%</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {dynamicRule
                      ? `Descuento calculado: -${formatClientPricingMoney(rowState.discountAmount)}`
                      : fixedRulePendingConversion
                        ? `Fijo — pendiente de conversión. ${row?.conversionIssue?.message || 'Revisa el reporte de conversión o ingresa un porcentaje para reemplazarlo.'}`
                        : 'Ingresa un porcentaje para crear una regla dinámica.'}
                  </p>
                </div>
              )}
              {marginWarning && <p className="mt-1 max-w-[240px] text-xs font-medium text-amber-700">{marginWarning}</p>}
            </td>
            <td className="px-3 py-2 text-right text-sm font-medium text-gray-700">
              {rowState.discountedMargin === null
                ? <span className="text-gray-400">-</span>
                : formatClientPricingMoney(rowState.discountedMargin)}
            </td>
            <td className="px-3 py-2 text-right text-sm font-semibold text-green-700">
              {renderFinalPriceLabel({
                finalPrice: rowState.finalPrice,
                referenceCost: row.referenceCost,
              })}
            </td>
          </>
        ) : costPlusMode ? (
          dynamicRule ? (
            <td colSpan={2} className="px-3 py-2">
              <div className="flex min-w-[330px] items-center justify-between gap-3 rounded border border-blue-100 bg-blue-50 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-blue-800">Regla dinámica · {dynamicRuleLabel}</p>
                  <p className="mt-0.5 text-xs text-blue-700">
                    Precio actual: {formatClientPricingMoney(rowState.finalPrice)}. Cambiar de modo no congela esta regla.
                  </p>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => replaceDynamicRule(row)}
                    disabled={disabledRow}
                    className="flex-none rounded border border-blue-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                  >
                    Reemplazar por regla fija
                  </button>
                )}
              </div>
            </td>
          ) : (
          <>
            <td className="px-3 py-2">
              {readOnly ? (
                <span className="flex justify-end text-right text-xs font-medium">
                  {renderReferenceMarkupLabel(finalMarkup)}
                </span>
              ) : (
                <>
                  <div className="flex min-w-[205px] items-center gap-2">
                    <select
                      aria-label="Tipo de margen"
                      value={benefitType}
                      onChange={(event) => setRowMarginType(row, event.target.value)}
                      className="h-9 rounded border border-gray-300 bg-white px-2 text-sm"
                      disabled={disabledRow}
                    >
                      <option value="amount">Monto</option>
                      <option value="percent">%</option>
                    </select>
                    <input
                      aria-label="Margen sobre costo"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={benefitInputValue}
                      onChange={(event) => setRowCostPlusDraft(row, 'benefitValue', event.target.value)}
                      onBlur={() => commitRowDraft(row)}
                      onKeyDown={(event) => handleRowCommitKeyDown(event, row)}
                      className={`h-9 w-24 rounded border px-2 text-sm ${hasFinalPriceError ? 'border-red-300' : 'border-gray-300'}`}
                      disabled={disabledRow}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {finalMarkup.amount === null
                      ? 'Costo no disponible'
                      : `Equivale a ${formatClientPricingMoney(finalMarkup.amount)} · ${formatReferenceMarkupPercent(finalMarkup.percent)}`}
                  </p>
                </>
              )}
            </td>
            <td className="px-3 py-2">
              {readOnly ? (
                <span className="flex justify-end text-right text-sm font-semibold text-green-700">
                  {renderFinalPriceLabel({
                    finalPrice: rowState.finalPrice,
                    referenceCost: row.referenceCost,
                  })}
                </span>
              ) : (
                <>
                  <div className="ml-auto flex min-w-[150px] items-center justify-end gap-1">
                    <span className="text-sm font-semibold text-gray-500">$</span>
                    <input
                      aria-label="Precio final"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={finalPriceInputValue}
                      onChange={(event) => setRowCostPlusDraft(row, 'finalPrice', event.target.value)}
                      onBlur={() => commitRowDraft(row)}
                      onKeyDown={(event) => handleRowCommitKeyDown(event, row)}
                      className={`h-9 w-28 rounded border px-2 text-right text-sm font-semibold ${hasFinalPriceError
                        ? 'border-red-300 text-red-700'
                        : hasNegativeFinalMargin
                          ? 'border-amber-300 text-amber-800'
                          : 'border-green-300 text-green-700'}`}
                      disabled={disabledRow}
                    />
                  </div>
                  <p className={`mt-1 text-right text-xs ${hasFinalPriceError
                    ? 'text-red-600'
                    : hasNegativeFinalMargin
                      ? 'text-amber-700'
                      : 'text-green-600'}`}>
                    {row._costPlusError || (finalMarkup.amount === null
                      ? 'Costo no disponible'
                      : `Margen: ${formatClientPricingMoney(finalMarkup.amount)} · ${formatReferenceMarkupPercent(finalMarkup.percent)}`)}
                  </p>
                </>
              )}
            </td>
          </>
          )
        ) : (
          <>
            <td className="px-3 py-2 text-right text-xs font-medium">
              {renderReferenceMarkupLabel(referenceMarkup)}
            </td>
            <td className="px-3 py-2">
              {dynamicRule ? (
                <div className="min-w-[230px] rounded border border-blue-100 bg-blue-50 px-2.5 py-2">
                  <p className="text-xs font-semibold text-blue-800">Regla dinámica · {dynamicRuleLabel}</p>
                  <p className="mt-0.5 text-xs text-blue-700">-{formatClientPricingMoney(rowState.discountAmount)} hoy</p>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => replaceDynamicRule(row)}
                      disabled={disabledRow}
                      className="mt-2 rounded border border-blue-200 bg-white px-2 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                    >
                      Reemplazar por regla fija
                    </button>
                  )}
                </div>
              ) : !readOnly ? (
                <div className="flex min-w-[190px] items-center gap-2">
                  <select
                    value={row.discountType || 'amount'}
                    onChange={(event) => setRowDraftField(row, 'discountType', event.target.value)}
                    onBlur={() => commitRowDraft(row)}
                    onKeyDown={(event) => handleRowCommitKeyDown(event, row)}
                    className="h-9 rounded border border-gray-300 px-2 text-sm"
                    disabled={disabledRow}
                  >
                    <option value="amount">Monto</option>
                    <option value="percent">%</option>
                  </select>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    value={getRowDiscountValue(row)}
                    onChange={(event) => setRowDraftField(row, 'discountValue', event.target.value)}
                    onBlur={() => commitRowDraft(row)}
                    onKeyDown={(event) => handleRowCommitKeyDown(event, row)}
                    className="h-9 w-24 rounded border border-gray-300 px-2 text-sm"
                    disabled={disabledRow}
                  />
                </div>
              ) : rowState.discountAmount > 0 ? (
                <p className="text-sm font-medium text-gray-700">
                  {row.discountType === 'percent'
                    ? `${getRowDiscountValue(row)}%`
                    : formatClientPricingMoney(getRowDiscountValue(row))}
                </p>
              ) : null}
              {!dynamicRule && (
                <p className="mt-1 text-xs text-gray-500">
                  {rowState.discountAmount > 0 ? `-${formatClientPricingMoney(rowState.discountAmount)}` : 'Sin descuento'}
                </p>
              )}
            </td>
            <td className="px-3 py-2 text-right text-sm font-semibold text-green-700">
              {renderFinalPriceLabel({
                finalPrice: rowState.finalPrice,
                referenceCost: row.referenceCost,
              })}
            </td>
          </>
        )}
        <td className="px-3 py-2 text-right">
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${status.className}`}>
            {status.label}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <section className={`min-w-0 rounded-lg border border-gray-200 bg-white p-4 ${className}`.trim()}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {loading && <InlineLoading label="Cargando precios" />}
          {saved && <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Guardado</span>}
          {!readOnly && (
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
              {allSelectedRows.length} seleccionados
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <ProductSearchInput
          type="search"
          value={search}
          onChange={(event) => onSearchChange?.(event.target.value)}
          placeholder="Buscar producto, variedad o formato"
          products={searchProducts}
          suggestionVisibility={filtersReady ? undefined : false}
          className="h-10 w-full min-w-0 rounded border border-gray-300 px-3 text-sm"
          disabled={disabled}
        />

        <fieldset>
          <legend className="mb-2 text-xs font-semibold text-gray-600">Formatos</legend>
          <div className="flex flex-wrap items-center gap-2">
            {clientTypeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={selectedClientType === option.value}
                title={option.label}
                onClick={() => setSelectedClientType((current) => (
                  current === option.value ? '' : option.value
                ))}
                className={`max-w-[12rem] truncate rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  selectedClientType === option.value
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
            {!clientTypeOptions.length && (
              <p className="text-sm text-gray-500">No hay formatos disponibles.</p>
            )}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-xs font-semibold text-gray-600">Categoría</legend>
          <CategoryFilterBar
            categories={categoryOptions}
            selectedCategory={selectedCategory}
            onSelect={(value) => setSelectedCategory((current) => (
              current === String(value) ? '' : String(value)
            ))}
            includeAll={false}
            itemsWrapperClassName="pb-1"
            emptyMessage="No hay categorias disponibles."
            isEmpty={categoryOptions.length === 0}
          />
        </fieldset>

        {!readOnly && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  {marginDiscountMode
                    ? 'Selección y descuento sobre margen'
                    : costPlusMode ? 'Seleccion y margen' : 'Seleccion y descuento'}
                </div>
                <p className="text-xs text-gray-500">
                  Selecciona precios visibles y aplica cambios solo sobre esa seleccion.
                </p>
              </div>
              <span className="w-fit rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                {filteredRows.length} visibles
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setRowsSelected(filteredRows, true)}
                disabled={disabled || loading || !filteredRows.length || visibleSelection.allSelected}
                className="rounded border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
              >
                Seleccionar visibles
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                disabled={disabled || !selectedIds.size}
                className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Quitar seleccion
              </button>
              {!marginDiscountMode && (
                <select
                  value={costPlusMode
                    ? resolvedBulkForm.benefitType || 'amount'
                    : resolvedBulkForm.discountType || 'amount'}
                  onChange={(event) => setBulkField(
                    costPlusMode ? 'benefitType' : 'discountType',
                    event.target.value
                  )}
                  className="h-10 rounded border border-gray-300 bg-white px-2 text-sm"
                  disabled={disabled}
                >
                  <option value="amount">{costPlusMode ? 'Margen monto ($)' : 'Monto'}</option>
                  <option value="percent">{costPlusMode ? 'Margen porcentaje (%)' : '%'}</option>
                </select>
              )}
              <input
                aria-label={marginDiscountMode
                  ? normalizedMarginDisplay === 'retained'
                    ? 'Porcentaje masivo de margen conservado'
                    : 'Porcentaje masivo de reducción de margen'
                  : undefined}
                type="text"
                inputMode={marginDiscountMode ? 'decimal' : 'numeric'}
                pattern={marginDiscountMode ? undefined : '[0-9]*'}
                autoComplete="off"
                value={marginDiscountMode
                  ? resolvedBulkForm.marginPercent ?? ''
                  : costPlusMode ? resolvedBulkForm.benefitValue ?? '' : resolvedBulkForm.discountValue ?? ''}
                onChange={(event) => setBulkField(
                  marginDiscountMode ? 'marginPercent' : costPlusMode ? 'benefitValue' : 'discountValue',
                  event.target.value
                )}
                placeholder={marginDiscountMode
                  ? '20'
                  : (costPlusMode ? resolvedBulkForm.benefitType : resolvedBulkForm.discountType) === 'percent'
                    ? '10'
                    : '2000'}
                className="h-10 w-24 rounded border border-gray-300 bg-white px-2 text-sm"
                disabled={disabled}
              />
              {marginDiscountMode && <span className="text-sm font-semibold text-gray-500">%</span>}
              {!marginDiscountMode && selectedDynamicRuleCount > 0 && (
                <label className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                  <input
                    type="checkbox"
                    checked={replaceDynamicRulesInBulk}
                    onChange={(event) => setReplaceDynamicRulesInBulk(event.target.checked)}
                    disabled={disabled || savingBulk}
                  />
                  Reemplazar {selectedDynamicRuleCount === 1
                    ? '1 regla dinámica por una fija'
                    : `${selectedDynamicRuleCount} reglas dinámicas por reglas fijas`}
                </label>
              )}
              <button
                type="button"
                onClick={() => handleBulk('apply')}
                disabled={disabled || loading || savingBulk || !filtersReady || !selectedIds.size}
                className="rounded bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
              >
                Aplicar
              </button>
              <button
                type="button"
                onClick={() => handleBulk('clear')}
                disabled={disabled || loading || savingBulk || !filtersReady || !selectedIds.size}
                className="rounded border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                {costPlusMode ? 'Restablecer precio base' : 'Quitar descuentos'}
              </button>
            </div>
          </div>
        )}
      </div>

      {(error || localError) && (
        <p className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
          {error || localError}
        </p>
      )}

      <div className="mt-4 max-h-[34rem] max-w-full overflow-auto rounded-lg border border-gray-200">
        <table className={`${marginDiscountMode ? 'min-w-[1340px]' : costPlusMode ? 'min-w-[1040px]' : 'min-w-[1120px]'} w-full text-left text-sm`}>
          <thead className="sticky top-0 z-10 bg-gray-100 text-xs uppercase text-gray-500">
            <tr>
              <th className="w-10 px-3 py-2"></th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Formato</th>
              <th className="px-3 py-2 text-right">Costo ref.</th>
              <th className="px-3 py-2 text-right">Precio base</th>
              {marginDiscountMode ? (
                <>
                  <th className="px-3 py-2 text-right">Margen positivo</th>
                  <th className="px-3 py-2">
                    {normalizedMarginDisplay === 'retained' ? '% de margen conservado' : '% de reducción'}
                  </th>
                  <th className="px-3 py-2 text-right">Margen final</th>
                  <th className="px-3 py-2 text-right">Final</th>
                </>
              ) : costPlusMode ? (
                <>
                  <th className="px-3 py-2">Margen sobre costo</th>
                  <th className="px-3 py-2 text-right">Precio final</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2 text-right">Margen</th>
                  <th className="px-3 py-2">Descuento</th>
                  <th className="px-3 py-2 text-right">Final</th>
                </>
              )}
              <th className="px-3 py-2 text-right">Estado</th>
            </tr>
          </thead>
          <tbody>
            {productGroups.length ? productGroups.map(renderProductRow) : (
              <tr>
                <td colSpan={dataColumnCount + 1} className="px-3 py-8 text-center text-sm text-gray-500">
                  {loading
                    ? 'Cargando precios...'
                    : !filtersReady
                      ? 'Selecciona una categoría y un formato para ver los descuentos.'
                      : emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ClientPricingDiscountMatrix;
